import algoliasearch from "algoliasearch/lite";
import tableSearch from "./../shared/tableSearch/tableSearch.vue";
import ProductApi from "../../../services/api/productApi.js";
import DuplicateProductModal from "./duplicateProductModal/duplicateProductModal.vue";
import ImportProductsModal from "./importProductsModal/importProductsModal.vue";
import { mapGetters } from "vuex";
import { shopifyApi } from "@/services/api/shopifyApi";
import FileSaver from "file-saver";

const ProductApiService = new ProductApi(),
  mainCategories = [
    "apparel",
    "jewelry",
    "accessories",
    "body",
    "living",
    "ready to wear",
    "tees",
    "outwear",
    "bags",
    "frills",
    "flair",
    "stationary",
    "home",
    "gifts",
    "kids & baby",
  ];

export default {
  components: {
    tableSearch,
    DuplicateProductModal,
    ImportProductsModal,
  },

  data() {
    return {
      productBeingSaved: null,
      productCopy: null,
      isDuplicateProductModalVisible: false,
      sortByDateDropdown: [{ name: "Newest to Oldest" }, { name: "Oldest to Newest" }],
      filterByRoleDropdown: [
        { name: "ALL" },
        { name: "Created by Admin", created_by_admin: true },
        { name: "Created by Brand", created_by_admin: false },
      ],
      productsCreatedByAdminFilter: null,
      selectedBrand: null,
      selectedCategory: null,
      selectedAvailability: null,
      algoliaClient: algoliasearch(this.$env.algolia().appId, this.$env.algolia().token, {
        timeouts: {
          connect: this.$utils.constants.ALGOLIA_CONNECT_TIMEOUT,
          read: this.$utils.constants.ALGOLIA_READ_TIMEOUT,
        },
      }),
      brands: [],
      categories: [],
      availabilities: [
        { name: "ALL", value: null },
        { name: "Available", value: true },
        { name: "Not Available", value: false },
      ],
      query: "",
      isTypingSearchQuery: false,
      searchIndexProducts: null,
      products: [],
      pagination: {
        page: 1,
        limit: 12,
        totalCount: 0,
      },
      tabLinks: [
        { name: "Products", path: "products", routeName: "dashboardProducts" },
        { name: "Collections", path: "collections", routeName: "dashboardCollections" },
        { name: "Categories", path: "categories", routeName: "dashboardCategories" },
      ],
      isImportProductsModalVisible: false,
      isShopifyConnected: undefined,
      showShopifyButton: false,
      loaded: false,
    };
  },

  beforeMount() {
    this.$loader.$emit("trigger", true);
    this.searchIndexProducts = this.algoliaClient.initIndex(this.$env.getOrigin() + "_products");
    this.searchIndexCategories = this.algoliaClient.initIndex(this.$env.getOrigin() + "_categories");

    if (this.$route.query.page) {
      this.pagination.page = Number(this.$route.query.page);
    }

    if (this.$route.query.productBeingSaved) {
      this.productBeingSaved = this.$route.query.productBeingSaved;
    }

    this.getCategories();
    this.getProducts();

    if (this.user.role === "ADMIN" || this.user.role === "PRODUCT_BUILDER") {
      this.getBrands();
    }
  },

  beforeRouteLeave(to, from, next) {
    const _self = this;

    if (_self.productBeingSaved) {
      if (!(to.name === "editProduct" && to.params.productId === _self.productBeingSaved)) {
        next();
      }
    } else {
      next();
    }
  },

  mounted() {
    this.$eventBus.$on("clear-saving-product", this.clearSavingProductCallback);
    this.$eventBus.$on("product-saved", this.productSavedCallback);
    this.checkShopifyState();

    // TODO uncomment these lines when want to activate shopify
    // if(this.user.role === 'BRAND'){
    //     this.showShopifyButton = true;
    // }
  },

  beforeDestroy() {
    this.$eventBus.$off("clear-saving-product", this.clearSavingProductCallback);
    this.$eventBus.$off("product-saved", this.productSavedCallback);
  },

  methods: {
    productSavedCallback,
    sortDateChangeCallback,
    filterRoleChangeCallback,
    getProducts,
    getBrands,
    getCategories,
    setSearchHistory,
    getProductsCallback,
    removeProductConfirm,
    removeProduct,
    search,
    redirect,
    brandChangeCallback,
    productTypeChangeCallback,
    availAbilityChangeCallback,
    showDuplicateProductModal,
    closeDuplicateProductModal,
    toggleVisibleAttribute,
    clearSavingProductCallback,
    brandsFetched,
    closeImportProductsModal,
    imgUrl,
    connectShopify,
    checkShopifyState,
    getCategoryNames,
    exportProducts,
    isUserRole,
    isExportDisabled,
    getProductID
  },

  computed: {
    ...mapGetters({
      user: "userState",
    }),
  },
};

function productSavedCallback() {
  this.getProducts(this.pagination.page);
}

function closeImportProductsModal() {
  this.isImportProductsModalVisible = false;
}

function clearSavingProductCallback() {
  this.productBeingSaved = null;
  if (this.pagination.page === 1) {
    this.getProducts(this.pagination.page);
  }
}

function toggleVisibleAttribute(e, id, index) {
  e.preventDefault();

  this.$loader.$emit("trigger", true);

  ProductApiService.changeVisibleAttribute(id)
    .then(() => {
      this.products = this.products.map((product) =>
        product.id === id
          ? {
              ...product,
              visible: !product.visible,
            }
          : product
      );
    })
    .catch((error) => {
      if (error.response) {
        this.$swal("Attention", error.response.data.error_message);
      } else {
        this.$bugsnagClient.notify(new Error(error));
      }
    })
    .finally(() => {
      this.$loader.$emit("trigger", false);
    });
}

function showDuplicateProductModal(product) {
  this.productCopy = product;
  this.isDuplicateProductModalVisible = true;
}

function closeDuplicateProductModal(pullProducts) {
  this.productCopy = null;
  this.isDuplicateProductModalVisible = false;

  if (pullProducts) {
    this.getProducts();
  }
}

function sortDateChangeCallback(val) {
  if (val.name === "Newest to Oldest") {
    this.searchIndexProducts = this.algoliaClient.initIndex(this.$env.getOrigin() + "_products_created_at_asc");
  } else {
    this.searchIndexProducts = this.algoliaClient.initIndex(this.$env.getOrigin() + "_products_created_at_desc");
  }

  this.pagination.page = 1;
  this.getProducts();
}

function filterRoleChangeCallback(data) {
  if (data.name === "ALL") {
    this.productsCreatedByAdminFilter = null;
  } else {
    this.productsCreatedByAdminFilter = data.created_by_admin;
  }

  this.getProducts();
}

function redirect(id) {
  let routeQuery = {
    page: this.pagination.page,
  };

  if (this.query.length) routeQuery.product_search_keyword = this.query;
  if (this.selectedBrand) routeQuery.brand = this.selectedBrand.id;
  if (this.selectedCategory) routeQuery.category = this.selectedCategory.id;
  if (this.selectedAvailability) routeQuery.active = this.selectedAvailability.name;

  let routeData = this.$router.resolve({
    name: "editProduct",
    params: { productId: id },
    query: routeQuery,
  });

  window.open(routeData.href, "_blank");
}

function brandChangeCallback(value) {
  this.selectedBrand = value;
  this.getProducts(1);
}

function brandsFetched(brands) {
  this.brands = brands;
}

function productTypeChangeCallback(value) {
  this.selectedCategory = value;
  this.getProducts();
}

function availAbilityChangeCallback(value) {
  this.selectedAvailability = value;
  this.getProducts();
}

function search(val) {
  this.pagination.page = 1;
  this.query = val;
  this.getProducts();
}

function removeProductConfirm(id) {
  let _self = this;

  _self
    .$swal({
      title: "Attention",
      text: "Are you sure you want to delete this product?",
      showCancelButton: true,
      cancelButtonText: "Cancel",
      confirmButtonText: "Confirm",
    })
    .then(function (confirm) {
      if (confirm.value) {
        _self.removeProduct(id);
      } else {
      }
    });
}

function removeProduct(id) {
  ProductApiService.removeProduct(id)
    .then((response) => {
      this.$swal("Success", "Product successfully removed");
      this.getProducts();
    })
    .catch((error) => {
      this.$swal("Attention", error.response.data.error_message);
    });
}

async function getBrands() {
  try {
    const { data } = await this.$utils.getAllBrands();

    this.brands = data.brands_dropdown;
    this.brands.unshift({ name: "ALL" });
  } catch (error) {
    this.$swal("Attention", this.$utils.formatErrorMessage("brands"));
  }
}

async function getCategories() {
  this.categories = (await this.$utils.getProductCategories()).map((container) => {
    return {
      ...container,
      view_components: container.children_containers.length
        ? container.children_containers.flatMap((_container) => _container.view_components)
        : container.view_components,
    };
  });
}

function setSearchHistory() {
  let _self = this;

  if (_self.$route.query.page) _self.pagination.page = Number(_self.$route.query.page);
  if (_self.$route.query.product_search_keyword) _self.query = _self.$route.query.product_search_keyword;

  if (_self.$route.query.brand) {
    _self.brands.forEach(function (brand) {
      if (brand.id === _self.$route.query.brand) {
        _self.selectedBrand = brand;
      }
    });
  }

  if (_self.$route.query.category) {
    _self.categories.forEach(function (category) {
      if (category.id === _self.$route.query.category) {
        _self.selectedCategory = category;
      }
    });
  }

  if (_self.$route.query.active) {
    _self.selectedAvailability = {
      name: _self.$route.query.active,
    };

    switch (_self.selectedAvailability.name) {
      case "ALL":
        _self.selectedAvailability.value = null;
        break;

      case "Available":
        _self.selectedAvailability.value = true;
        break;

      case "Not Available":
        _self.selectedAvailability.value = false;
        break;
    }
  }

  _self.$router.replace("/dashboard/products");
  _self.getProducts();
}

function getProducts(page) {
  if (page) this.pagination.page = page;
  this.searchIndexProducts.clearCache();
  this.searchIndexProducts.search(
    {
      query: this.query,
      distinct: false,
      page: this.pagination.page - 1,
      hitsPerPage: this.pagination.limit,
      facets: "*,active,brand.id,categories.id,product_status",
      facetFilters: [
        this.productsCreatedByAdminFilter === null || this.productsCreatedByAdminFilter === "ALL"
          ? []
          : "created_by_admin:" + this.productsCreatedByAdminFilter,
        this.user && this.user.role === "BRAND" ? ["brand.id:" + this.user.brand_id] : [],
        this.selectedAvailability === null || this.selectedAvailability.name === "ALL"
          ? []
          : "active:" + this.selectedAvailability.value,
        this.selectedBrand === null || this.selectedBrand.name === "ALL" ? [] : "brand.id:" + this.selectedBrand.id,
        this.selectedCategory === null || this.selectedCategory.name === "ALL"
          ? []
          : "categories.id:" + this.selectedCategory.entity_id,
        ["product_status: APPROVED"],
      ],
    },
    this.getProductsCallback
  );
}

function getProductsCallback(err, content) {
  let _self = this;

  if (err) {
    _self.$swal("Attention", _self.$utils.formatErrorMessage("products"));

    return;
  }
  _self.pagination.limit = content.hitsPerPage;
  _self.pagination.totalCount = content.nbHits;

  _self.products = content.hits;

  this.loaded = true;

  _self.$forceUpdate();
  this.$loader.$emit("trigger", false);
}

function imgUrl(image) {
  if (typeof image === "string") {
    return image;
  } else {
    return image.image;
  }
}

async function connectShopify() {
  if (this.isShopifyConnected) {
    return;
  }
  this.$router.push("connect-shopify");
}

async function checkShopifyState() {
  try {
    const { connected } = await shopifyApi.getState();
    this.isShopifyConnected = !!connected;
  } catch (e) {
    this.isShopifyConnected = false;
  }
}

function getCategoryNames(product) {
  let categoryNames = [],
    { categories, category } = product;

  if (categories) {
    categoryNames = categories.map((item) => item.name).join(", ");
  } else {
    categoryNames = category?.name;
  }

  return categoryNames;
}

function isUserRole(role) {
  return this.user?.role === role;
}

async function exportProducts() {
  if (this.isExportDisabled()) {
    return;
  }

  try {
    const response = await ProductApiService.exportProducts();
    const fileToSave = new File([response.data], "bulletin_products.csv", {
      type: "application/csv",
    });
    FileSaver.saveAs(fileToSave);
  } catch (e) {
    this.$swal("Alert", "Something went wrong. Please try again later.");
  }
}

function isExportDisabled() {
  return !this.loaded || !this.products?.length;
}

function getProductID(product) {
  return product.id ? product.id : product.objectID;
}